// @flow
import * as React from 'react';
import styled from 'styled-components';
// eslint-disable-next-line no-restricted-imports
import PrimaryPalette from '../colors';
import { Grid } from '../layouts/grid';
import { Body, SectionHeader } from '../typography';

/**
 * SafeError is inaccessible outside of this file.
 *
 * We only want to throw the error in development environments to enforce correct component usage.
 * In any other kind of environments, the errors should be ignored.
 */
class SafeError extends Error {
  name: string;
  message: string;

  constructor(message: string) {
    super(message);
    this.message = message;
    this.name = this.constructor.name;
  }
}

class SafeThrower {
  static safeThrow(message: string) {
    const nodeEnvDefined = process && process.env && process.env.NODE_ENV;
    const canSafeThrow =
      nodeEnvDefined && process.env.NODE_ENV === 'development';

    if (canSafeThrow) {
      throw new SafeError(message);
    }
  }
}

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.104.0. To view the error, delete this comment and run Flow. */
const ErrorBoundaryWrapper = styled(Grid)`
  background: ${PrimaryPalette.darkYellow};
  border-radius: 5px;
  padding: 4px 12px;
`;

type Props = { children: React.Node };
type State = { error: ?Error };

class SafeErrorBoundary extends React.Component<Props, State> {
  state = { error: null };

  static getDerivedStateFromError(error: Error) {
    if (error instanceof SafeError) {
      return { error };
    }
  }

  componentDidCatch(_error: Error, _errorInfo: mixed) {}

  render() {
    const { error } = this.state;
    if (error) {
      return (
        <ErrorBoundaryWrapper
          container
          spacing={3}
          data-provides="safe-error-boundary"
        >
          <Grid item>
            <SectionHeader>Component usage error:</SectionHeader>
            <Body>{error.message}</Body>
          </Grid>
        </ErrorBoundaryWrapper>
      );
    }

    return this.props.children;
  }
}

export { SafeThrower, SafeErrorBoundary };
